// What-if overlay: explore the recommended option AND the runner-up a couple
// of levels deep (on defaults), side by side, with a verdict. Cross-branch
// curiosity for the price of one keystroke; nothing touches the real graph.

import { state } from "../core/state";
import { compareFutures, exploreFuture, type FutureStep } from "../core/whatif";
import { $, el } from "./dom";
import { toast } from "./toast";

let wiRun = 0;

export async function whatIf(): Promise<void> {
  const node = state.nodes.get(state.currentId!);
  if (!node || node.options.length < 2) { toast("need a node with at least two options"); return; }
  const run = ++wiRun;
  const a = node.recommend ?? 0;
  const b = a === 0 ? 1 : 0;   // runner-up: the first non-recommended option
  $("#wiOverlay").classList.add("show");
  $("#wiMeta").textContent = node.question;
  $("#wiVerdict").textContent = "";
  const cols: Record<"A" | "B", HTMLElement> = { A: $("#wiA"), B: $("#wiB") };
  const seed = (col: "A" | "B", idx: number) => {
    cols[col].innerHTML = "";
    cols[col].appendChild(el("div", "wi-head", `${col} · ${node.options[idx]!.label}`));
    const list = el("div", "wi-steps");
    list.dataset.col = col;
    list.appendChild(el("div", "wi-wait", "exploring on defaults…"));
    cols[col].appendChild(list);
  };
  seed("A", a); seed("B", b);
  const addStep = (col: "A" | "B") => (step: FutureStep) => {
    if (run !== wiRun) return;
    const list = cols[col].querySelector(".wi-steps")!;
    list.querySelector(".wi-wait")?.remove();
    const s = el("div", "wi-step");
    s.appendChild(el("div", "wi-q", step.question));
    s.appendChild(el("div", "wi-take", `→ ${step.take}`));
    list.appendChild(s);
    list.appendChild(el("div", "wi-wait", "…"));
  };
  try {
    const [aSteps, bSteps] = await Promise.all([
      exploreFuture(node, a, 2, addStep("A")),
      exploreFuture(node, b, 2, addStep("B")),
    ]);
    if (run !== wiRun) return;
    document.querySelectorAll("#wiOverlay .wi-wait").forEach((w) => w.remove());
    $("#wiVerdict").textContent = "judging the two futures…";
    const v = await compareFutures(node, a, b, aSteps, bSteps);
    if (run !== wiRun) return;
    cols[v.pick].classList.add("wi-winner");
    $("#wiVerdict").innerHTML = "";
    $("#wiVerdict").appendChild(el("div", "wi-why", `⚖ ${v.pick} — ${v.why}`));
    $("#wiVerdict").appendChild(el("div", "wi-risk", `A risk: ${v.aRisk}`));
    $("#wiVerdict").appendChild(el("div", "wi-risk", `B risk: ${v.bRisk}`));
  } catch {
    if (run === wiRun) $("#wiVerdict").textContent = "couldn't finish the exploration — rate limit, try again";
  }
}

export function closeWhatIf(): void {
  wiRun++;
  $("#wiOverlay").classList.remove("show");
  $("#wiA").classList.remove("wi-winner");
  $("#wiB").classList.remove("wi-winner");
}
